/**
 * Public share-link API helpers (unauthenticated `/share/:token` surface).
 *
 * The gating metadata is a typed Eden call; the sealed HTML document is fetched
 * raw (the endpoint returns `text/html`, not an envelope) and injected into a
 * sandboxed iframe by the viewer. The token lives ONLY in the URL the recipient
 * was given — these helpers never persist or log it.
 */

import type { CompanyApi } from "../../hooks/useCompanyApi"
import { apiUrl } from "../runtime-origin"
import { api } from "./client"
import { extractErrorMessage, unwrapData } from "./errors"

export type TShareState =
  | "ok"
  | "login-required"
  | "password"
  | "expired"
  | "revoked"
  | "unshareable"
  | "not-found"
  | "error"

export interface IShareBrand {
  product: string
  name: string
  accent: string
  logoDataUri?: string
}

export interface IShareMeta {
  state: TShareState
  kind?: string
  brand?: IShareBrand
}

/** Fetch the gating metadata for a share token (state + brand chrome). Never throws. */
export async function fetchShareMeta(token: string): Promise<IShareMeta> {
  try {
    const { data, error } = await api.api.shares({ token }).get()
    if (error || !data) return { state: "error" }
    return data as IShareMeta
  } catch {
    return { state: "error" }
  }
}

export interface IShareHtmlResult {
  ok: boolean
  /** Present when `ok`. The sealed, self-contained HTML document. */
  html?: string
  /** HTTP status on failure (401 = password required/wrong, 410 = gone, 429 = rate-limited). */
  status?: number
  message?: string
}

/**
 * Fetch the sealed HTML document for a share. GET for open links; POST with the
 * password for gated links. Returns a discriminated result rather than throwing so
 * the viewer can render clean gate/error states.
 */
export async function fetchShareHtml(token: string, password?: string): Promise<IShareHtmlResult> {
  const url = apiUrl(`/api/shares/${encodeURIComponent(token)}/view`)
  // credentials: "include" — an "authenticated" (members-only) share is gated on
  // the viewer's Better Auth session cookie.
  const res =
    password === undefined
      ? await fetch(url, { method: "GET", credentials: "include" })
      : await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ password }),
        })

  if (res.ok) return { ok: true, html: await res.text() }
  const body = await res.json().catch(() => ({}))
  return { ok: false, status: res.status, message: extractErrorMessage(body) }
}

// ---------------------------------------------------------------------------
// Authenticated mint / list / revoke (company-scoped, via useCompanyApi client)
// ---------------------------------------------------------------------------

export type TShareKind = "summary" | "report"
export type TShareAccessMode = "public" | "authenticated"

export interface IShareSummary {
  id: number
  kind: string
  accessMode: string
  artifactId: number | null
  hasPassword: boolean
  expiresAt: string
  createdAt: string
  createdBy: string
  viewCount: number
  lastViewedAt: string | null
}

export interface IShareList {
  shares: IShareSummary[]
  maxExpiryDays: number
  defaultExpiryDays: number
}

export interface ICreateShareInput {
  kind: TShareKind
  /** 'public' (anyone with the link — default) or 'authenticated' (signed-in org members only). */
  accessMode?: TShareAccessMode
  artifactId?: number
  expiresInDays?: number
  password?: string
}

/** List the active share links for a meeting (never returns tokens — only metadata). */
export async function listMeetingShares(company: CompanyApi, meetingId: string): Promise<IShareList> {
  return unwrapData(await company.meetings({ id: meetingId }).shares.get()) as IShareList
}

/** Mint a share link. Returns the raw token ONCE — the caller must surface it immediately. */
export async function createMeetingShare(
  company: CompanyApi,
  meetingId: string,
  input: ICreateShareInput,
): Promise<{ token: string; share: IShareSummary }> {
  return unwrapData(await company.meetings({ id: meetingId }).shares.post(input)) as {
    token: string
    share: IShareSummary
  }
}

/** Revoke a share link by id. */
export async function revokeMeetingShare(company: CompanyApi, shareId: number): Promise<{ revoked: boolean }> {
  return unwrapData(await company.shares({ shareId: String(shareId) }).delete()) as { revoked: boolean }
}
